import React, { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useConfig } from '@lore/config';
import { PayloadStates } from '@lore/utils';
import { useClientAsyncInit } from '@statsig/react-bindings';
import { browserName, osName } from 'react-device-detect';

import FeatureFlagsContext from './FeatureFlagsContext';

/**
 * Guard against Statsig being blocked (e.g. by Privacy Badger) or slow to
 * resolve: after this timeout we stop waiting and treat the flags as
 * resolved. Kept short (5s) so a blocked/slow Statsig doesn't strand the
 * user on a loading state — or flash the legacy setup pages — for long after
 * login while routing waits on the streamlined-account flag.
 */
export const FLAG_TIMEOUT_MS = 5000;

/** Identity used by the client until the real user is known. */
const ANONYMOUS_USER_ID = 'unauthenticated';

/**
 * The identity the flag values currently in the client belong to. Comparing
 * it against the identity we *want* is what tells us whether a gate answer
 * can be trusted, so a value fetched for the anonymous user is never mistaken
 * for the signed-in user's value.
 *
 * Returns undefined while the current user is still being fetched — at that
 * point we don't know yet who we're asking about.
 */
function getUserKey(currentUser) {
  if (
    !currentUser ||
    currentUser.state === PayloadStates.FETCHING ||
    currentUser.state === PayloadStates.INITIAL_STATE
  ) {
    return undefined;
  }
  const id = currentUser.data?.id;
  if (!id) return ANONYMOUS_USER_ID;
  return `${id}|${currentUser.data?.email ?? ''}`;
}

function isLinux(name) {
  const LinuxOsNames = [
    'Arch', 'CentOS', 'Fedora', 'Debian', 'Deepin', 'Gentoo', 'Joli', 'Linpus',
    'Linspire', 'Linux', 'Maemo', 'Mageia', 'Mandriva', 'Manjaro', 'MeeGo',
    'Mint', 'PCLinuxOS', 'Raspbian', 'RedHat', 'Sabayon', 'Sailfish',
    'Slackware', 'SUSE', 'Tizen', 'Ubuntu', 'VectorLinux', 'Zenwalk',
    'elementary OS'
  ];
  return LinuxOsNames.indexOf(name) !== -1;
}

FeatureFlagsProvider.propTypes = {
  currentUser: PropTypes.object,
  children: PropTypes.node
};

/**
 * Creates and owns the ONE Statsig client for the application.
 *
 * Every consumer (Master, useAccountSetup, useCTAs, the setup pages, ...)
 * reads the flags from this provider through useFeatureFlags, so they all
 * observe the same client, the same "user is applied" state and the same
 * timeout. Previously each call site ran its own useClientAsyncInit, which
 * meant one component could still see the gate as false (client initialized
 * for 'unauthenticated', user not applied yet) while another already saw the
 * real value — and whichever redirect won that race decided where the user
 * landed.
 *
 * @param {object} props
 * @param {import('../models/currentUser').LoreModel} props.currentUser - The current user model.
 * @param {React.ReactNode} props.children
 */
export default function FeatureFlagsProvider({ currentUser, children }) {
  const config = useConfig();

  // The identity the client's cached flag values were actually fetched for.
  const [appliedUserKey, setAppliedUserKey] = useState(null);

  const [timedOut, setTimedOut] = useState(false);
  const timeoutRef = useRef(null);

  const userKey = getUserKey(currentUser);
  const isUserKnown = userKey !== undefined;

  const { client, isLoading: isFetching } = useClientAsyncInit(
    config.statsig.apiKey,
    {
      userID: currentUser?.data?.id ?? 'unauthenticated',
      email: currentUser?.data?.email,
      browserName,
      custom: { os_name: isLinux(osName) ? 'Linux' : osName }
    },
    { environment: { tier: config.statsig.environment } }
  );

  // The clock only starts once we know which user we're asking about,
  // otherwise a slow currentUser fetch alone can burn the whole budget and we
  // give up while holding nothing but the anonymous user's flag values.
  useEffect(() => {
    if (!isUserKnown) return undefined;
    timeoutRef.current = setTimeout(() => {
      setTimedOut(true);
    }, FLAG_TIMEOUT_MS);
    return () => clearTimeout(timeoutRef.current);
  }, [isUserKnown]);

  // Apply the real user to the client as soon as we know who they are. Until
  // that round trip completes the client still holds the ANONYMOUS user's
  // values, so appliedUserKey is only advanced once the new values are in —
  // never up front. Advancing it also re-renders every consumer, which is how
  // they learn that the gate answers changed.
  useEffect(() => {
    if (!client) return undefined;
    // Current user not fetched yet — we don't know who to ask about.
    if (!isUserKnown) return undefined;
    if (appliedUserKey === userKey) return undefined;

    // The client was constructed with the anonymous user, so its values
    // already belong to that identity; no round trip needed.
    if (userKey === ANONYMOUS_USER_ID) {
      setAppliedUserKey(userKey);
      return undefined;
    }

    let cancelled = false;

    client.updateUserAsync({
      userID: currentUser.data.id,
      email: currentUser.data.email,
      browserName,
      custom: { os_name: isLinux(osName) ? 'Linux' : osName }
    })
      .then(() => {
        if (!cancelled) setAppliedUserKey(userKey);
      })
      .catch(() => {
        console.error('statsig user update failed')
        if (!cancelled) setAppliedUserKey(userKey);
      });

    return () => {
      cancelled = true;
    };
  }, [client, isUserKnown, userKey, appliedUserKey]);

  const isUserReady = isUserKnown && appliedUserKey === userKey;

  const value = useMemo(() => {
    const checkGate = (gate) => {
      const notReady = isFetching || !client || !isUserReady;
      if (!timedOut && notReady) return undefined;
      if (notReady) return false; // timed out and still not ready
      return client.checkGate(gate);
    };

    return {
      isFetching: !timedOut && (isFetching || !isUserReady),
      client,
      getStreamlinedAccountFlowEnabled: () => checkGate('streamlined_account_creation'),
      getFellowshipCTA: () => checkGate('cta_fellowship_training'),
      getFeedbackCTA: () => checkGate('cta_feedback_survey'),
      getLowerNetworkBarriersStewards: () => checkGate('lower_network_barriers_for_stewards')
    };
  }, [client, isFetching, isUserReady, timedOut]);

  return (
    <FeatureFlagsContext.Provider value={value}>
      {children}
    </FeatureFlagsContext.Provider>
  );
};
